/**
 * constants/legends.js
 *
 * Централизованное хранилище конфигураций всех легенд приложения
 *
 * Структура:
 * - Простые типизированные легенды (COLORED_ITEMS, RANGE)
 * - Сложные кастомные легенды (TERRAIN - Aspect, Slope, Composite)
 * - Легенды фильтров (Classification, Time-based)
 *
 * Миграция:
 * - Старые данные находились в constants/variables.js
 * - Компоненты находились в components/FilterSidebar/*Legend/
 * - Новая система: данные здесь, компоненты в LegendRenderer/
 */

import React from 'react';
import { defineMessage } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { LEGEND_TYPES, LEGEND_ICON_TYPES } from '../types/legendTypes';
import {
    HOUSES_STYLE,
    SCOOTER_DEFICIT_STYLE,
    SCOOTER_PROFICIT_STYLE,
    REBALANCE_PARKING_STYLE,
    PARKING_DENSITY_STYLE,
    SMOOTHED_RENT_STYLE,
    ROAD_ROUGHNESS_STYLE,
    BOOKING_PARKING_STYLE,
    PARKINGS_BOOKINGS_DATE_STYLE,
    AGGREGATED_TRACKS_AUGUST_STYLE,
    AGGREGATED_TRIPS_AUGUST_H3_STYLE,
    AGGREGATED_STARTS_AUGUST_STYLE,
    AGGREGATED_FINISHES_AUGUST_STYLE,
    RECHARGING_STATS_STYLE,
    MEDIAN_SPEED_STYLE,
    WALKSCORE_STYLE,
    DYNAMIC_TARIFF_STYLE,
    DYNAMIC_TARIFF_ZONES_STYLE,
    POI_STYLE,
    NEW_BUILDINGS_STYLE,
    CLASS_PARKING_TYPES_STYLE,
    LANDUSE_STYLE,
    LANDUSE_NEW_STYLE,
    POPULATION_STYLE,
    SLOPE_STYLE,
    PARKING_RADIUS_STYLE,
    CLASSIFIED_PARKING_STYLE,
    BIKE_PATH_STYLE,
    SCR_TRACKS_STYLE,
    SUPPOSED_BIKE_PATH_STYLE,
    CLUSTERED_TAPS_STYLE,
    PARKING_PROPOSALS_STYLE,
    CLUSTERED_OPENS_STYLE,
    AGG_USER_OPENS_7DAYS_STYLE,
    AGG_USER_OPENS_30DAYS_STYLE,
    AGG_USER_TAPS_ZERO_STARTS_7DAYS_STYLE,
    AGG_USER_TAPS_ZERO_STARTS_30DAYS_STYLE,
    AGG_USER_OPENS_TO_STARTS_STYLE,
    REG_TO_TRIP_CONVERSION_STYLE,
    AGGREGATED_DIFF_START_FINISH_STYLE,
    AGGREGATED_DIFF_START_FINISH_AUGUST_STYLE,
    AGG_FAILED_TRIPS_7DAYS_STYLE,
    CHARGING_OBJECTS_LOAD_STYLE,
    CHARGING_PROPOSALS_STYLE,
    CHARGING_PROPOSALS_SCOOTER_STYLE,
    CHARGING_PROPOSALS_PLAN_STYLE,
    DOWNTIME_SCOOTERS_STYLE,
    WHOOSH_PARKING_CAPACITY_STYLE,
    WHOOSH_HOTSPOT_STYLE,
    WHOOSH_PARKING_TYPE_STYLE,
    STARTS_BY_HOUR_SIZE_STYLE,
    STARTS_BY_HOUR_COLORS_STYLE,
    HEX_PARKINGS_GRADIENT,
    OPERATION_AREAS_ROTATION_GRADIENT,
    OPERATION_AREAS_ROTATION_2_GRADIENT,
    OA_TRIPS_GRADIENT,
    OA_FLEET_GRADIENT,
    RESTRICTION_ZONES_STYLE,
    REBALANCE_FORECAST_STYLE,
    REBALANCE_FORECAST_SIZE_STYLE,
    REBALANCE_DELTA_STYLE,
    FACT_REBALANCE_COLORS_STYLE,
    FACT_REBALANCE_SIZE_STYLE,
    ADMIN_AGG_TRACKS_V2_STYLE,
    AGG_TRACKS_COHORT_SHARE_STYLE,
    USER_COHORTS_SHARE_STYLE,
    LOST_DEVICES_STYLE,
    LOST_DEVICES_H3_STYLE,
    WIFI_AP_OBS_STYLE,
    WIFI_AP_H3_STYLE,
    ROUTE_ANOMALIES_H3_STYLE,
    SIM_ACCIDENTS_STYLE,
} from './layerStyles';

/**
 * CurDeficitScooters легенда - показатель дефицита самокатов на парковке
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const CUR_DEFICIT_SCOOTERS_LEGEND = {
    id: 'cur-deficit-scooters',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Дефицит самокатов на парковке' }),
    style: SCOOTER_DEFICIT_STYLE,
    styleKey: 'deficit',
};

/**
 * CurProficitScooters легенда - показатель избытка самокатов на парковке
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const CUR_PROFICIT_SCOOTERS_LEGEND = {
    id: 'cur-proficit-scooters',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Профицит самокатов на парковке' }),
    style: SCOOTER_PROFICIT_STYLE,
    styleKey: 'proficit',
};

/**
 * RebalanceParking легенда - прогноз баланса парковок (дефицит ↔ избыток)
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const REBALANCE_PARKING_LEGEND = {
    id: 'rebalance-parking',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Показатель дефицита/избытка на парковке' }),
    style: REBALANCE_PARKING_STYLE,
    styleKey: 'balance',
    renderConfig: {
        description: defineMessage({ message: 'Прогноз избытка/дефицита на следующие 6 часов' }),
    },
};

/**
 * RebalanceForecast легенда — форкаст ребаланса
 * Тип: COLORED_ITEMS
 * Иконка: CIRCLE
 * cluster=0 → профицит (жёлтый), cluster=4/5 → дефицит (красный)
 */
export const REBALANCE_FORECAST_LEGEND = {
    id: 'rebalance-forecast-v2',
    type: LEGEND_TYPES.COLORED_ITEMS,
    icon: LEGEND_ICON_TYPES.CIRCLE,
    style: REBALANCE_FORECAST_STYLE,
    styleKey: 'categories',
    renderConfig: {
        iconSize: '14px',
        iconOpacity: 0.85,
    },
};

/**
 * RebalanceForecast размерная легенда — градуировка радиуса по полю forecast
 * Тип: SIZE_GRADIENT
 */
export const REBALANCE_FORECAST_SIZE_LEGEND = {
    id: 'rebalance-forecast-v2-size',
    type: LEGEND_TYPES.SIZE_GRADIENT,
    title: defineMessage({ message: 'Форкаст ребаланса:' }),
    style: REBALANCE_FORECAST_SIZE_STYLE,
    styleKey: 'forecast',
    renderConfig: {
        minRadius: 5,
        maxRadius: 18,
    },
};

/**
 * RebalanceDelta легенда — таба ребаланса (forecast − cnt_scooters)
 * Тип: COLORED_ITEMS
 * delta > 0 → дефицит (красный), delta ≤ 0 → профицит (жёлтый)
 */
export const REBALANCE_DELTA_LEGEND = {
    id: 'rebalance-delta',
    type: LEGEND_TYPES.COLORED_ITEMS,
    icon: LEGEND_ICON_TYPES.CIRCLE,
    style: REBALANCE_DELTA_STYLE,
    styleKey: 'categories',
    renderConfig: {
        iconSize: '14px',
        iconOpacity: 0.85,
    },
};

/**
 * RebalanceDelta размерная легенда — градуировка радиуса по |delta|
 * Тип: SIZE_GRADIENT
 */
export const REBALANCE_DELTA_SIZE_LEGEND = {
    id: 'rebalance-delta-size',
    type: LEGEND_TYPES.SIZE_GRADIENT,
    title: defineMessage({ message: 'Таба ребаланса:' }),
    style: REBALANCE_DELTA_STYLE,
    styleKey: 'delta',
    renderConfig: {
        minRadius: 4,
        maxRadius: 27,
    },
};

// ============================================================
// ВСПОМОГАТЕЛЬНЫЕ ОБЪЕКТЫ ДЛЯ СОВМЕСТИМОСТИ
// ============================================================

/**
 * Карта легенд для быстрого доступа по ID
 */

// ============================================================
// ПРОСТЫЕ ТИПИЗИРОВАННЫЕ ЛЕГЕНДЫ (COLORED_ITEMS)
// ============================================================

/**
 * POI легенда - категории точек интереса (Food, Shop, Leisure и т.д.)
 * Тип: COLORED_ITEMS
 * Иконка: CIRCLE
 * Items подтягиваются из стиля слоя через styleKey
 */
export const POI_LEGEND = {
    id: 'poi',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Категории POI:' }),
    icon: LEGEND_ICON_TYPES.CIRCLE,
    style: POI_STYLE,
    styleKey: 'categories',
    renderConfig: {
        containerClass: 'innerLegendGrid',
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

/**
 * New Buildings легенда - типы новых зданий
 * Тип: COLORED_ITEMS
 * Иконка: CIRCLE
 * Items подтягиваются из стиля слоя через styleKey
 */
export const NEW_BUILDINGS_LEGEND = {
    id: 'new-buildings',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Новые здания:' }),
    icon: LEGEND_ICON_TYPES.CIRCLE,
    style: NEW_BUILDINGS_STYLE,
    styleKey: 'types',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

/**
 * Landuse легенда - типы землепользования
 * Тип: COLORED_ITEMS
 * Иконка: RECT
 */

const LanduseDescription = () => (
    <div>
        <span style={{ display: 'block', fontSize: '14px', paddingLeft: '10px' }}>
            {i18n._('Плохое:')}
        </span>
        <span style={{ display: 'block', color: 'rgb(220, 217, 217)', fontSize: '12px', textAlign: 'left', margin: 0, paddingLeft: '10px' }}>
            {i18n._('промышленные территории, гаражи, порты, лес, военные, железнодорожные объекты, реки и др.')}
        </span>
        <br />
        <span style={{ display: 'block', fontSize: '14px', paddingLeft: '10px' }}>
            {i18n._('Хорошее:')}
        </span>
        <span style={{ display: 'block', color: 'rgb(220, 217, 217)', fontSize: '12px', textAlign: 'left', margin: 0, paddingLeft: '10px' }}>
            {i18n._('жилые районы, коммерция, образовательные учреждения и др.')}
        </span>
        <br />
    </div>
);

export const LANDUSE_LEGEND = {
    id: 'landuse',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Землепользование (Данные OSM):' }),
    icon: LEGEND_ICON_TYPES.RECT,
    style: LANDUSE_STYLE,
    styleKey: 'categories',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
        description: <LanduseDescription />,
    },
};

// DA-15980 — описание нового варианта того же слоя. Категории те же три (плюс «не
// классифицировано»), но набираются они не из тегов OSM, а из классов типологии застройки,
// поэтому и пояснение другое: важно, по какому признаку территория попала в категорию.
const LanduseNewDescription = () => (
    <div>
        <span style={{ display: 'block', fontSize: '14px', paddingLeft: '10px' }}>
            {i18n._('Плохое:')}
        </span>
        <span style={{ display: 'block', color: 'rgb(220, 217, 217)', fontSize: '12px', textAlign: 'left', margin: 0, paddingLeft: '10px' }}>
            {i18n._('зелёные территории, промышленность, образование, здравоохранение, спорт, транспортные хабы — заведомо закрытый или ограниченный проезд')}
        </span>
        <br />
        <span style={{ display: 'block', fontSize: '14px', paddingLeft: '10px' }}>
            {i18n._('Хорошее:')}
        </span>
        <span style={{ display: 'block', color: 'rgb(220, 217, 217)', fontSize: '12px', textAlign: 'left', margin: 0, paddingLeft: '10px' }}>
            {i18n._('многоквартирная застройка, коммерция, кампусы ВУЗов, благоустроенное озеленение')}
        </span>
        <br />
        <span style={{ display: 'block', fontSize: '14px', paddingLeft: '10px' }}>
            {i18n._('Частный сектор:')}
        </span>
        <span style={{ display: 'block', color: 'rgb(220, 217, 217)', fontSize: '12px', textAlign: 'left', margin: 0, paddingLeft: '10px' }}>
            {i18n._('индивидуальная жилая застройка — определяется по самим зданиям, а не по тегу landuse')}
        </span>
        <br />
        <span style={{ display: 'block', color: 'rgb(220, 217, 217)', fontSize: '12px', textAlign: 'left', margin: 0, paddingLeft: '10px' }}>
            {i18n._('Дороги и вода не входят в квартальную сетку и остаются незакрашенными.')}
        </span>
        <br />
    </div>
);

export const LANDUSE_NEW_LEGEND = {
    id: 'landuse-new',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Землепользование (Типология территорий):' }),
    icon: LEGEND_ICON_TYPES.RECT,
    style: LANDUSE_NEW_STYLE,
    styleKey: 'categories',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
        description: <LanduseNewDescription />,
    },
};

// ============================================================
// ДИАПАЗОННЫЕ ЛЕГЕНДЫ (RANGE)
// ============================================================

/**
 * Population легенда - плотность населения по ячейкам
 * Тип: COLORED_ITEMS
 * Иконка: HEXAGON
 */
export const POPULATION_LEGEND = {
    id: 'population',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Население в ячейке:' }),
    icon: LEGEND_ICON_TYPES.HEXAGON,
    style: POPULATION_STYLE,
    styleKey: 'density',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

/**
 * Parking Radius легенда - расстояния до парковок
 * Тип: COLORED_ITEMS
 * Иконка: CIRCLE
 */
export const PARKING_RADIUS_LEGEND = {
    id: 'parking-radius',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Радиус парковки:' }),
    icon: LEGEND_ICON_TYPES.CIRCLE,
    style: PARKING_RADIUS_STYLE,
    styleKey: 'distances',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

/**
 * Classified Parking легенда - статусы парковок (Дефицит/Баланс/Профицит)
 * Тип: COLORED_ITEMS
 * Иконка: CIRCLE
 */
export const CLASSIFIED_PARKING_LEGEND = {
    id: 'classified-parking',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Классификация парковок:' }),
    icon: LEGEND_ICON_TYPES.CIRCLE,
    style: CLASSIFIED_PARKING_STYLE,
    styleKey: 'classification',
    renderConfig: {
        containerClass: 'innerLegendGrid',
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

/**
 * Class Parking Types легенда - классы парковок (Топовые, Средние, Минимальные)
 * Тип: COLORED_ITEMS
 * Иконка: CIRCLE
 * Используется в фильтрах классификации парковок
 */
export const CLASS_PARKING_TYPES_LEGEND = {
    id: 'class-parking-types',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Класс парковки:' }),
    icon: LEGEND_ICON_TYPES.CIRCLE,
    style: CLASS_PARKING_TYPES_STYLE,
    styleKey: 'types',
    renderConfig: {
        containerClass: 'innerLegendGrid',
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

// ============================================================
// КАСТОМНЫЕ ЛЕГЕНДЫ (TERRAIN)
// ============================================================

/**
 * Slope легенда - уклоны рельефа
 * Тип: COLORED_ITEMS (использует стандартный renderer с прямоугольными иконками)
 * Мигрирована на новую систему легенд (Этап 2)
 */
export const SLOPE_LEGEND = {
    id: 'slope',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Уклоны:' }),
    icon: LEGEND_ICON_TYPES.RECT,
    style: SLOPE_STYLE,
    styleKey: 'slopes',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

/**
 * Aspect легенда - экспозиция склонов (N, NE, E и т.д.)
 * Тип: CUSTOM (использует собственный компонент AspectLegend с SVG)
 * Отображает круговую диаграмму с компасом
 */
export const ASPECT_LEGEND = {
    id: 'aspect',
    type: LEGEND_TYPES.CUSTOM,
    title: defineMessage({ message: 'Экспозиция:' }),
    // customRenderer будет AspectLegendComponent
    items: [
        { name: 'N', angle: 90, color: '#84d600' },
        { name: 'NE', angle: 45, color: '#00ab44' },
        { name: 'E', angle: 0, color: '#0068c0' },
        { name: 'SE', angle: 315, color: '#6c00a3' },
        { name: 'S', angle: 270, color: '#ca009c' },
        { name: 'SW', angle: 225, color: '#ff5568' },
        { name: 'W', angle: 180, color: '#ffab47' },
        { name: 'NW', angle: 135, color: '#f4fa00' },
    ],
};

/**
 * Composite Slope/Aspect легенда - комбинированная визуализация
 * Тип: CUSTOM (использует собственный компонент CompositeLegend)
 * Отображает радиальную диаграмму со слоями slope и лучами aspect
 */
export const COMPOSITE_LEGEND = {
    id: 'composite',
    type: LEGEND_TYPES.CUSTOM,
    title: defineMessage({ message: 'Наложение склонов и экспозиции:' }),
    // customRenderer будет CompositeLegenComponent
};

// ============================================================
// ЛИНИИ И ПУТИ (LINE типы)
// ============================================================

/**
 * Bike Path легенда - типы велодорожек
 * Тип: COLORED_ITEMS
 * Иконка: LINE (зелёные и голубые линии)
 */
export const BIKE_PATH_LEGEND = {
    id: 'bike-path',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Велодорожки:' }),
    icon: LEGEND_ICON_TYPES.LINE,
    style: BIKE_PATH_STYLE,
    styleKey: 'types',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
        strokeWidth: 3,
    },
};

// DA-14933 — легенда треков исполнителей (роль исполнителя)
export const SCR_TRACKS_LEGEND = {
    id: 'scr-tracks',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Роль исполнителя:' }),
    icon: LEGEND_ICON_TYPES.LINE,
    style: SCR_TRACKS_STYLE,
    styleKey: 'roles',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.85,
        strokeWidth: 3,
    },
};

/**
 * Supposed Bike Path легенда - этапы предлагаемых велодорожек
 * Тип: COLORED_ITEMS
 * Иконка: LINE (жёлтая, голубая, серая линии)
 */
export const SUPPOSED_BIKE_PATH_LEGEND = {
    id: 'supposed-bike-path',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Предлагаемые велодорожки:' }),
    icon: LEGEND_ICON_TYPES.LINE,
    style: SUPPOSED_BIKE_PATH_STYLE,
    styleKey: 'stages',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
        strokeWidth: 3,
    },
};

/**
 * Clustered Taps легенда - кластеризированные тапы пользователей
 * Тип: COLORED_ITEMS
 * Иконка: HEXAGON
 * Используется для анализа Local Moran's I
 */
// DA-15433 — предложения по парковкам
export const PARKING_PROPOSALS_LEGEND = {
    id: 'parking-proposals',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Величина спроса (место в городе):' }),
    icon: LEGEND_ICON_TYPES.RECT,
    style: PARKING_PROPOSALS_STYLE,
    styleKey: 'demand',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

export const CLUSTERED_TAPS_LEGEND = {
    id: 'clustered-taps',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Кластеризированные тапы пользователей:' }),
    icon: LEGEND_ICON_TYPES.HEXAGON,
    style: CLUSTERED_TAPS_STYLE,
    styleKey: 'clusters',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

/**
 * ClusteredOpens легенда - кластеризированные открытия пользователей
 * Тип: COLORED_ITEMS
 * Иконка: HEXAGON
 */
export const CLUSTERED_OPENS_LEGEND = {
    id: 'clustered-opens',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Кластеризированные открытия пользователей' }),
    icon: LEGEND_ICON_TYPES.HEXAGON,
    style: CLUSTERED_OPENS_STYLE,
    styleKey: 'clusters',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

/**
 * AggUserOpens легенды - агрегированные открытия приложения
 * Тип: COLORED_ITEMS
 * Иконка: HEXAGON
 * Примечание: Две версии - 7 дней и 30 дней
 */
export const AGG_USER_OPENS_LEGEND_7DAYS = {
    id: 'agg-user-opens-7days',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Агрегированные открытия приложения' }),
    icon: LEGEND_ICON_TYPES.HEXAGON,
    style: AGG_USER_OPENS_7DAYS_STYLE,
    styleKey: 'ranges',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

export const AGG_USER_OPENS_LEGEND_30DAYS = {
    id: 'agg-user-opens-30days',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Агрегированные открытия приложения' }),
    icon: LEGEND_ICON_TYPES.HEXAGON,
    style: AGG_USER_OPENS_30DAYS_STYLE,
    styleKey: 'ranges',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

/**
 * AggUserTapsZeroStarts легенды - агрегированные скважины без стартов
 * Тип: COLORED_ITEMS
 * Иконка: HEXAGON
 */
export const AGG_USER_TAPS_ZERO_STARTS_LEGEND_7DAYS = {
    id: 'agg-user-taps-zero-starts-7days',
    type: LEGEND_TYPES.COLORED_ITEMS,
    icon: LEGEND_ICON_TYPES.HEXAGON,
    style: AGG_USER_TAPS_ZERO_STARTS_7DAYS_STYLE,
    styleKey: 'ranges',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

export const AGG_USER_TAPS_ZERO_STARTS_LEGEND_30DAYS = {
    id: 'agg-user-taps-zero-starts-30days',
    type: LEGEND_TYPES.COLORED_ITEMS,
    icon: LEGEND_ICON_TYPES.HEXAGON,
    style: AGG_USER_TAPS_ZERO_STARTS_30DAYS_STYLE,
    styleKey: 'ranges',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

/**
 * Конверсия из регистраций в старты (DA-13570)
 * Тип: COLORED_ITEMS
 * Иконка: HEXAGON
 */
export const REG_TO_TRIP_CONVERSION_LEGEND = {
    id: 'reg-to-trip-conversion',
    type: LEGEND_TYPES.COLORED_ITEMS,
    icon: LEGEND_ICON_TYPES.HEXAGON,
    title: defineMessage({ message: 'Конверсия из регистраций в старты (за последние 3 дня)' }),
    style: REG_TO_TRIP_CONVERSION_STYLE,
    styleKey: 'ranges',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

/**
 * AggUserOpensToStarts легенда - соотношение открытий к стартам
 * Тип: COLORED_ITEMS
 * Иконка: HEXAGON
 */
export const AGG_USER_OPENS_TO_STARTS_LEGEND = {
    id: 'agg-user-opens-to-starts',
    type: LEGEND_TYPES.COLORED_ITEMS,
    icon: LEGEND_ICON_TYPES.HEXAGON,
    style: AGG_USER_OPENS_TO_STARTS_STYLE,
    styleKey: 'ratios',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

/**
 * AggregatedDiffStartFinish легенды - разница между стартами и финишами
 * Тип: COLORED_ITEMS
 * Иконка: HEXAGON
 */
export const AGGREGATED_DIFF_START_FINISH_LEGEND = {
    id: 'agg-diff-start-finish',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Разница стартов/финишей' }),
    icon: LEGEND_ICON_TYPES.HEXAGON,
    style: AGGREGATED_DIFF_START_FINISH_STYLE,
    styleKey: 'differences',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

export const AGGREGATED_DIFF_START_FINISH_LEGEND_AUGUST = {
    id: 'agg-diff-start-finish-august',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Разница стартов/финишей (август)' }),
    icon: LEGEND_ICON_TYPES.HEXAGON,
    style: AGGREGATED_DIFF_START_FINISH_AUGUST_STYLE,
    styleKey: 'differences',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

/**
 * AggFailedTrips легенда - доля неудачных поездок
 * Тип: COLORED_ITEMS
 * Иконка: HEXAGON
 */
export const AGG_FAILED_TRIPS_LEGEND_7DAYS = {
    id: 'agg-failed-trips-7days',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Доля неудачных поездок' }),
    icon: LEGEND_ICON_TYPES.HEXAGON,
    style: AGG_FAILED_TRIPS_7DAYS_STYLE,
    styleKey: 'percentages',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

/**
 * AggHexSpeed легенда - медианная скорость поездок
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
// DA-14919: WalkScore по H3 (тестовый DEV-слой). Статическая шкала 0–100,
// одинаковая для всех метрик — переключатель меняет только крашенный атрибут.
export const WALKSCORE_LEGEND = {
    id: 'walkscore-h3',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'WalkScore (0–100)' }),
    style: WALKSCORE_STYLE,
    styleKey: 'score',
    renderConfig: {
    },
};


// DA-13733: загрузка зарядных объектов Москвы при обороте 3 АКБ на слот в сутки
export const CHARGING_OBJECTS_LOAD_LEGEND = {
    id: 'charging-objects-load',
    type: LEGEND_TYPES.COLORED_ITEMS,
    // Пояснение вынесено мелким текстом под легенду (ChargingObjectsLoadLegend)
    title: null,
    icon: LEGEND_ICON_TYPES.CIRCLE,
    style: CHARGING_OBJECTS_LOAD_STYLE,
    styleKey: 'utilization',
    renderConfig: {
        iconSize: '16px',
        iconOpacity: 0.9,
    },
};

// DA-13733: предложения по развитию сети — подлегенда под переключателем
export const CHARGING_PROPOSALS_LEGEND = {
    id: 'charging-proposals',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: null,
    icon: LEGEND_ICON_TYPES.RING,
    style: CHARGING_PROPOSALS_STYLE,
    styleKey: 'kinds',
    renderConfig: {
        iconSize: '16px',
        iconOpacity: 1,
    },
};

// DA-13733: второй вариант предложений — 15 мин на самокате
export const CHARGING_PROPOSALS_SCOOTER_LEGEND = {
    ...CHARGING_PROPOSALS_LEGEND,
    id: 'charging-proposals-scooter',
    style: CHARGING_PROPOSALS_SCOOTER_STYLE,
};

// DA-13733: согласованный план
export const CHARGING_PROPOSALS_PLAN_LEGEND = {
    ...CHARGING_PROPOSALS_LEGEND,
    id: 'charging-proposals-plan',
    style: CHARGING_PROPOSALS_PLAN_STYLE,
};


export const AGG_HEX_SPEED_LEGEND = {
    id: 'agg-hex-speed',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Медианная скорость (км/ч):' }),
    style: MEDIAN_SPEED_STYLE,
    styleKey: 'speed',
    renderConfig: {
    },
};

/**
 * ParkingDensity легенда - плотность парковок на км²
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const PARKING_DENSITY_LEGEND = {
    id: 'parking-density',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Плотность парковок (парковок / км²):' }),
    style: PARKING_DENSITY_STYLE,
    styleKey: 'density',
    renderConfig: {
        description: defineMessage({ message: 'Парковок / км²' }),
    },
};

/**
 * TransportIsochrones легенда - изохроны доступности от транспорта
 * Тип: CUSTOM (просто информационный текст)
 * Иконка: нет
 */
export const TRANSPORT_ISOCHRONES_LEGEND = {
    id: 'transport-isochrones',
    type: LEGEND_TYPES.CUSTOM,
    renderConfig: {
        customRenderer: 'TransportIsochrones',
    },
};

/**
 * ParkingDensity градиент легенда - плотность парковок (KDE)
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const PARKING_DENSITY_GRADIENT_LEGEND = {
    id: 'parking-density-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Плотность парковок (парковок / км²)' }),
    style: PARKING_DENSITY_STYLE,
    styleKey: 'density',
    renderConfig: {
        description: defineMessage({ message: 'Парковок / км²' }),
    },
};

/**
 * Сглаженные показатели аренды градиент легенда
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const SMOOTHED_RENT_GRADIENT_LEGEND = {
    id: 'smoothed-rent-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Сглаженные показатели аренды' }),
    style: SMOOTHED_RENT_STYLE,
    styleKey: 'rent',
    renderConfig: {
        description: defineMessage({ message: 'Персентили отдельно определены для каждого региона' }),
    },
};

/**
 * Неровность дороги градиент легенда (Road Index)
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const ROAD_ROUGHNESS_GRADIENT_LEGEND = {
    id: 'road-roughness-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Неровность дороги' }),
    style: ROAD_ROUGHNESS_STYLE,
    styleKey: 'roughness',
    renderConfig: {
        description: defineMessage({ message: 'Чем больше индекс, тем более неровная дорога' }),
    },
};

/**
 * Бронирование самокатов на парковке градиент легенда
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const BOOKING_PARKING_GRADIENT_LEGEND = {
    id: 'booking-parking-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Бронирование самокатов на парковке' }),
    style: BOOKING_PARKING_STYLE,
    styleKey: 'booking',
    renderConfig: {
        description: defineMessage({ message: 'Доля забронированных самокатов' }),
    },
};

/**
 * Бронирование самокатов на парковке по датам - градиент легенда
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const PARKINGS_BOOKINGS_DATE_GRADIENT_LEGEND = {
    id: 'parkings-bookings-date-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Бронирование самокатов на парковке' }),
    style: PARKINGS_BOOKINGS_DATE_STYLE,
    styleKey: 'bookingsDate',
    renderConfig: {
        description: defineMessage({ message: 'Чем больше и ярче парковка - тем больше бронирований на ней за выбранный период' }),
    },
};

/**
 * Агрегированные треки (август) градиент легенда
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const AGGREGATED_TRACKS_AUGUST_GRADIENT_LEGEND = {
    id: 'aggregated-tracks-august-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Количество треков в ячейке' }),
    style: AGGREGATED_TRACKS_AUGUST_STYLE,
    styleKey: 'tracks',
    renderConfig: {
    },
};

/**
 * Агрегированные поездки (август) динамическая градиентная легенда
 * Тип: GRADIENT с isDynamic: true
 * Градиентная шкала с автоматическим масштабированием по H3 zoom
 * Формула масштабирования: value * 7^(zoom - 10)
 * Базовые значения (для zoom 10): [300, 1000, 4000, 8000, 25000] (в единицах, автоформат до K/M)
 * Важно: использует ТУ ЖЕ формулу что и paint expression в слое!
 */
export const AGGREGATED_TRIPS_AUGUST_H3_GRADIENT_LEGEND = {
    id: 'aggregated-trips-august-h3-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Количество поездок в ячейке' }),
    style: AGGREGATED_TRIPS_AUGUST_H3_STYLE,
    styleKey: 'trips',
    isDynamic: true,
    dynamicConfig: {
        h3ZoomReduxKey: 'mapFilters.aggTripsAugustH3Zoom',
        baseZoom: 10,           // Эталонный уровень zoom
        scalingFactor: 7,       // 7 = количество дочерних H3 ячеек при переходе на уровень выше
        minZoom: 6,             // Минимальный H3 zoom
        maxZoom: 12,            // Максимальный H3 zoom
        baseValues: [ 300, 1000, 4000, 8000, 25000 ],  // Базовые значения в единицах для zoom 10
    },
    renderConfig: {
    },
};

/**
 * Агрегированные треки за последние 7 дней — динамическая градиентная легенда
 * Тип: GRADIENT с isDynamic: true
 * Масштабирование: count_all * 7^(zoom - 10), базовые значения для zoom=12
 * scalingFactor=7 (дочерних H3 ячеек при переходе zoom на 1 уровень выше)
 * baseZoom=10, baseValues в тысячах поездок
 */
export const ADMIN_AGG_TRACKS_V2_LEGEND = {
    id: 'admin-agg-tracks-v2-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Количество треков в ячейке' }),
    style: ADMIN_AGG_TRACKS_V2_STYLE,
    styleKey: 'tracks',
    isDynamic: true,
    dynamicConfig: {
        h3ZoomReduxKey: 'mapFilters.adminAggTracksV2H3Zoom',
        baseZoom: 10,
        scalingFactor: 7,
        minZoom: 6,
        maxZoom: 12,
        baseValues: [ 300, 1000, 4000, 8000, 25000 ],
    },
    renderConfig: {
        description: defineMessage({ message: 'за последние 7 дней' }),
    },
};

/**
 * DA-15259: Треки по моделям тарификации — абсолютные значения.
 * Та же динамическая шкала, что у 7-дневного слоя, но привязана к своему h3-зуму.
 * Когорты сильно различаются по объёму, поэтому шкала динамическая (квантили по
 * загруженным фичам) — редкая когорта не выцветает в прозрачность.
 */
export const AGG_TRACKS_COHORT_LEGEND = {
    id: 'agg-tracks-cohort-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Количество треков в ячейке' }),
    style: ADMIN_AGG_TRACKS_V2_STYLE,
    styleKey: 'tracks',
    isDynamic: true,
    dynamicConfig: {
        h3ZoomReduxKey: 'mapFilters.aggTracksCohortH3Zoom',
        baseZoom: 10,
        scalingFactor: 7,
        minZoom: 6,
        maxZoom: 12,
        baseValues: [ 300, 1000, 4000, 8000, 25000 ],
    },
    renderConfig: {
        description: defineMessage({ message: 'за последние 7 дней' }),
    },
};

/**
 * DA-15259: Треки по моделям тарификации — доля когорты в гексагоне.
 * Шкала статическая 0–100%: доля от зума не зависит.
 */
export const AGG_TRACKS_COHORT_SHARE_LEGEND = {
    id: 'agg-tracks-cohort-share-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Доля когорты в ячейке' }),
    style: AGG_TRACKS_COHORT_SHARE_STYLE,
    styleKey: 'share',
    renderConfig: {
        description: defineMessage({ message: 'от всех треков в ячейке за 7 дней' }),
    },
};

/**
 * DA-15415: Треки по когортам пользователей — абсолютные значения.
 * Динамическая шкала (квантили по загруженным фичам), привязанная к своему h3-зуму:
 * бакеты сильно различаются по объёму (age_lt18 — сотни тысяч поездок против 155 млн
 * у age_age18_45), на статической шкале редкий бакет выцвел бы в прозрачность.
 */
export const USER_COHORTS_LEGEND = {
    id: 'user-cohorts-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Количество треков в ячейке' }),
    style: ADMIN_AGG_TRACKS_V2_STYLE,
    styleKey: 'tracks',
    isDynamic: true,
    dynamicConfig: {
        h3ZoomReduxKey: 'mapFilters.userCohortsH3Zoom',
        baseZoom: 10,
        scalingFactor: 7,
        minZoom: 6,
        maxZoom: 12,
        baseValues: [ 300, 1000, 4000, 8000, 25000 ],
    },
    renderConfig: {
        description: defineMessage({ message: 'за последние 7 дней' }),
    },
};

/**
 * DA-15415: Треки по когортам пользователей — доля бакета в гексагоне.
 * Шкала статическая 0–100%: доля от зума не зависит.
 */
export const USER_COHORTS_SHARE_LEGEND = {
    id: 'user-cohorts-share-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Доля когорты в ячейке' }),
    style: USER_COHORTS_SHARE_STYLE,
    styleKey: 'share',
    renderConfig: {
        description: defineMessage({ message: 'от всех треков в ячейке за выбранный период' }),
    },
};

// DA-14786: легенда помесячного слоя треков. Та же шкала, что у 7-дневного,
// но привязана к h3-зуму помесячного слоя и с описанием «за выбранный месяц».
export const ADMIN_AGG_TRACKS_MONTHLY_LEGEND = {
    id: 'admin-agg-tracks-monthly-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Количество треков в ячейке' }),
    style: ADMIN_AGG_TRACKS_V2_STYLE,
    styleKey: 'tracks',
    isDynamic: true,
    dynamicConfig: {
        h3ZoomReduxKey: 'mapFilters.adminAggTracksMonthlyH3Zoom',
        baseZoom: 10,
        scalingFactor: 7,
        minZoom: 6,
        maxZoom: 12,
        baseValues: [ 300, 1000, 4000, 8000, 25000 ],
    },
    renderConfig: {
        description: defineMessage({ message: 'за выбранный месяц' }),
    },
};

/**
 * Кол-во переходов в Change Battery динамическая градиентная легенда
 * Тип: GRADIENT с isDynamic: true
 * Градиентная шкала с автоматическим масштабированием по зуму
 * Использует цвета из Viridis палитры (как в "Плотность парковок")
 * Базовые значения основаны на перцентилях для зума 10:
 * P5=1, P40=5, P60=14, P75=42, P85=100, P92=180, P95=250
 */
export const RECHARGING_STATS_H3_DYNAMIC_LEGEND = {
    id: 'recharging-stats-h3-dynamic',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Количество переходов в Change Battery в ячейке (ед.):' }),
    style: RECHARGING_STATS_STYLE,
    styleKey: 'recharges',
    isDynamic: true,
    dynamicConfig: {
        h3ZoomReduxKey: 'mapFilters.rechargingStatsH3Zoom',
        baseZoom: 10,
        scalingFactor: 3,
        minZoom: 8,
        maxZoom: 10,
        baseValues: [ 1, 5, 14, 42, 100, 180, 250 ],
    },
    renderConfig: {
        description: defineMessage({ message: 'за периоды 06.24 - 09.24 и 06.25 - 09.25' }),
    },
};

/**
 * CHB-переходы за rolling-окна 1/7/30 дней — динамическая градиентная легенда (DA-14276)
 * Тип: GRADIENT с isDynamic: true
 * Те же цвета и масштабирование что и RECHARGING_STATS_H3_DYNAMIC_LEGEND.
 * Описание периода подставляется динамически в RechargingStatsLegend.
 */
export const RECHARGING_DAILY_H3_DYNAMIC_LEGEND = {
    id: 'recharging-daily-h3-dynamic',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Количество переходов в Change Battery в ячейке (ед.):' }),
    style: RECHARGING_STATS_STYLE,
    styleKey: 'recharges',
    isDynamic: true,
    dynamicConfig: {
        h3ZoomReduxKey: 'mapFilters.rechargingStatsH3Zoom',
        baseZoom: 10,
        scalingFactor: 3,
        minZoom: 8,
        maxZoom: 10,
        baseValues: [ 1, 5, 14, 42, 100, 180, 250 ],
    },
    renderConfig: {},
};

/**
 * Агрегированные старты (август) градиент легенда
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const AGGREGATED_STARTS_AUGUST_GRADIENT_LEGEND = {
    id: 'aggregated-starts-august-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Количество стартов в ячейке' }),
    style: AGGREGATED_STARTS_AUGUST_STYLE,
    styleKey: 'starts',
    renderConfig: {
    },
};

/**
 * Агрегированные финиши (август) градиент легенда
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const AGGREGATED_FINISHES_AUGUST_GRADIENT_LEGEND = {
    id: 'aggregated-finishes-august-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Количество финишей в ячейке' }),
    style: AGGREGATED_FINISHES_AUGUST_STYLE,
    styleKey: 'finishes',
    renderConfig: {
    },
};

/**
 * Медианная скорость градиент легенда
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const MEDIAN_SPEED_GRADIENT_LEGEND = {
    id: 'median-speed-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Медианная скорость, км/ч' }),
    style: MEDIAN_SPEED_STYLE,
    styleKey: 'speed',
    renderConfig: {
    },
};

/**
 * DynamicTariffScore легенда - шкала тарифов (1-17)
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const DYNAMIC_TARIFF_GRADIENT_LEGEND = {
    id: 'dynamic-tariff-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Score:' }),
    style: DYNAMIC_TARIFF_STYLE,
    styleKey: 'score',
    renderConfig: {
    },
};

/**
 * DynamicTariffZones легенда - градация зон по коду (300-800)
 * Тип: COLORED_ITEMS
 * Items подтягиваются из стиля слоя через styleKey
 * Визуализирует: цветные прямоугольники для каждого кода зоны
 */
export const DYNAMIC_TARIFF_ZONES_LEGEND = {
    id: 'dynamic-tariff-zones',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Код зоны:' }),
    icon: LEGEND_ICON_TYPES.RECT,
    style: DYNAMIC_TARIFF_ZONES_STYLE,
    styleKey: 'zoneCode',
    renderConfig: {
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

/**
 * HousesPopulationLegend - легенда для слоя "Жилые дома" (Жителей)
 * Тип: SIZE_RANGE_HORIZONTAL
 * Items подтягиваются автоматически из стиля слоя через styleKey
 * Круги распределены горизонтально, подписи под ними
 */
export const HOUSES_POPULATION_LEGEND = {
    id: 'houses-population',
    type: LEGEND_TYPES.SIZE_RANGE_HORIZONTAL,
    title: defineMessage({ message: 'Жителей, чел.' }),
    style: HOUSES_STYLE,
    styleKey: 'populationScale',
    renderConfig: {
        minRadius: 2,
        maxRadius: 11,
    },
};

/**
 * HousesYearLegend - легенда для слоя "Жилые дома" (Год постройки)
 * Тип: GRADIENT
 * Items подтягиваются автоматически из стиля слоя через styleKey
 * Визуализирует: градиентную полосу с подписями (жёлтый -> розовый)
 */
export const HOUSES_YEAR_LEGEND = {
    id: 'houses-year',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Год постройки' }),
    style: HOUSES_STYLE,
    styleKey: 'yearGradient',
    renderConfig: {
    },
};

/**
 * WhooshParkingCapacity легенда - вместимость парковок Whoosh
 * Тип: SIZE_RANGE_HORIZONTAL
 * Items подтягиваются из стиля слоя через styleKey
 * Визуализирует: окружности разных размеров для категорий вместимости
 */
export const WHOOSH_PARKING_CAPACITY_LEGEND = {
    id: 'whoosh-parking-capacity',
    type: LEGEND_TYPES.SIZE_RANGE_HORIZONTAL,
    title: defineMessage({ message: 'Вместимость парковки' }),
    style: WHOOSH_PARKING_CAPACITY_STYLE,
    styleKey: 'capacity',
    renderConfig: {
        minRadius: 4,
        maxRadius: 10,
    },
};

/**
 * SimAccidents легенда размера - число пострадавших (ранено + погибло) в ДТП (DA-15040)
 * Тип: SIZE_RANGE_HORIZONTAL
 * Визуализирует: окружности размера для 1 и 2 пострадавших (фактический диапазон данных).
 * minRadius/maxRadius совпадают с радиусами SIM_ACCIDENTS_RADIUS для 1 и 2 (3.67 и 7 px).
 */
export const SIM_ACCIDENTS_SIZE_LEGEND = {
    id: 'sim-accidents-size',
    type: LEGEND_TYPES.SIZE_RANGE_HORIZONTAL,
    title: defineMessage({ message: 'Число пострадавших' }),
    style: SIM_ACCIDENTS_STYLE,
    styleKey: 'victims',
    renderConfig: {
        minRadius: 3.67,
        maxRadius: 7,
    },
};

/**
 * AggScootersQuantity легенда - среднее количество самокатов на парковке за день
 * Используется в слое "Агрегированный простой самокатов"
 * Тип: SIZE_RANGE_HORIZONTAL
 * Items подтягиваются из стиля слоя через styleKey
 * Визуализирует: окружности разных размеров (мало/среднее/много самокатов)
 */
export const AGG_SCOOTERS_QUANTITY_LEGEND = {
    id: 'agg-scooters-quantity',
    type: LEGEND_TYPES.SIZE_RANGE_HORIZONTAL,
    title: defineMessage({ message: 'Среднее количество самокатов на парковке за день' }),
    style: DOWNTIME_SCOOTERS_STYLE,
    styleKey: 'scooterQuantity',
    renderConfig: {
        minRadius: 6,
        maxRadius: 18,
    },
};

/**
 * StartsByHourColors легенда - типы стартов для слоя "Старты по часам"
 * Тип: COLORED_ITEMS
 * Items подтягиваются из стиля слоя через styleKey
 * Визуализирует: красные и синие точки (старты от парковки / парковка не найдена)
 */
export const STARTS_BY_HOUR_COLORS_LEGEND = {
    id: 'starts-by-hour-colors',
    type: LEGEND_TYPES.COLORED_ITEMS,
    style: STARTS_BY_HOUR_COLORS_STYLE,
    styleKey: 'types',
    icon: LEGEND_ICON_TYPES.CIRCLE,
    renderConfig: {
        iconSize: '16px',
        iconOpacity: 1,
        fontSize: '12px',
    },
};

/**
 * StartsByHourSize легенда - размеры кругов для слоя "Старты по часам"
 * Тип: SIZE_GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 * Визуализирует: концентрические круги разного размера (1, 10, 20 стартов)
 * Использует горизонтальные выносками для подписей
 */
export const STARTS_BY_HOUR_SIZE_LEGEND = {
    id: 'starts-by-hour-size',
    type: LEGEND_TYPES.SIZE_GRADIENT,
    title: defineMessage({ message: 'Starts by Hour' }),
    style: STARTS_BY_HOUR_SIZE_STYLE,
    styleKey: 'quantities',
    renderConfig: {
        minRadius: 3,
        maxRadius: 22,
    },
};

/**
 * FactRebalanceColors легенда — цвета действий для слоя "Фактический ребаланс"
 * 🔴 только увезли / 🟢 только привезли / 🔵 оба действия
 * Тип: COLORED_ITEMS
 */
export const FACT_REBALANCE_COLORS_LEGEND = {
    id: 'fact-rebalance-colors',
    type: LEGEND_TYPES.COLORED_ITEMS,
    style: FACT_REBALANCE_COLORS_STYLE,
    styleKey: 'actions',
    icon: LEGEND_ICON_TYPES.CIRCLE,
    renderConfig: {
        iconSize: '14px',
        iconOpacity: 0.85,
        fontSize: '12px',
    },
};

/**
 * FactRebalanceSize легенда — размер точки пропорционален суммарному числу перевозок
 * Тип: SIZE_GRADIENT
 */
export const FACT_REBALANCE_SIZE_LEGEND = {
    id: 'fact-rebalance-size',
    type: LEGEND_TYPES.SIZE_RANGE_HORIZONTAL,
    title: defineMessage({ message: 'Перевозок на парковке:' }),
    style: FACT_REBALANCE_SIZE_STYLE,
    styleKey: 'quantities',
    renderConfig: {
        minRadius: 4,
        maxRadius: 16,
    },
};

/**
 * WhooshHotspot легенда - стационарная парковка Whoosh
 * Тип: COLORED_ITEMS
 * Items подтягиваются из стиля слоя через styleKey
 * Визуализирует: зелёный круг для обозначения стационарной парковки
 */
export const WHOOSH_HOTSPOT_LEGEND = {
    id: 'whoosh-hotspot',
    type: LEGEND_TYPES.COLORED_ITEMS,
    style: WHOOSH_HOTSPOT_STYLE,
    styleKey: 'items',
};

/**
 * WhooshParkingType легенда - тип парковки Whoosh (различие по обводке)
 * Тип: CUSTOM
 * Items подтягиваются из стиля слоя через styleKey
 * Визуализирует: круги с разной обводкой для физических, виртуальных, велосипедных и hotspot парковок
 */
export const WHOOSH_PARKING_TYPE_LEGEND = {
    id: 'whoosh-parking-type',
    type: LEGEND_TYPES.CUSTOM,
    title: defineMessage({ message: 'Тип парковки' }),
    style: WHOOSH_PARKING_TYPE_STYLE,
    styleKey: 'types',
};

// ============================================================
// ВСПОМОГАТЕЛЬНЫЕ ОБЪЕКТЫ ДЛЯ СОВМЕСТИМОСТИ
// ============================================================

/**
 * CurScootersParking легенда - текущее количество самокатов на парковках
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const CUR_SCOOTERS_PARKING_GRADIENT_LEGEND = {
    id: 'cur-scooters-parking-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Количество самокатов' }),
    style: DOWNTIME_SCOOTERS_STYLE,
    styleKey: 'curScootersGradient',
    renderConfig: {
        description: defineMessage({ message: 'Чем краснее и больше значение, тем больше самокатов на парковке.' }),
    },
};

/**
 * ParkingDowntime48h легенда - простой самокатов (48+ ч)
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const PARKING_DOWNTIME_48H_GRADIENT_LEGEND = {
    id: 'parking-downtime-48h-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Количество самокатов' }),
    style: DOWNTIME_SCOOTERS_STYLE,
    styleKey: 'downtime48hGradient',
    renderConfig: {
        description: defineMessage({ message: 'Чем краснее и больше значение, тем больше самокатов на парковке с простоем более 48 часов в статусе STAND_BY. Данные обновляются каждые 15 минут.' }),
    },
};

/**
 * AggHexForParkings легенда - индекс потенциала гексагона для размещения парковок
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const HEX_PARKINGS_GRADIENT_LEGEND = {
    id: 'hex-parkings-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Индекс потенциала гексагона для размещения парковок' }),
    items: HEX_PARKINGS_GRADIENT,
    renderConfig: {
        description: defineMessage({ message: 'Индекс определен для каждого региона' }),
    },
};

/**
 * OperationAreasRotation легенда - период ротации для операционных зон
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const OPERATION_AREAS_ROTATION_GRADIENT_LEGEND = {
    id: 'operation-areas-rotation-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Rotation' }),
    items: OPERATION_AREAS_ROTATION_GRADIENT,
};

/**
 * OperationAreasRotation2 легенда - период ротации для операционных зон v2
 * Тип: GRADIENT
 * Items подтягиваются из стиля слоя через styleKey
 */
export const OPERATION_AREAS_ROTATION_2_GRADIENT_LEGEND = {
    id: 'operation-areas-rotation-2-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Rotation' }),
    items: OPERATION_AREAS_ROTATION_2_GRADIENT,
};

/**
 * DA-12016: Легенда метрики "Старты" для операционных зон
 * Тип: GRADIENT
 */
export const OA_TRIPS_GRADIENT_LEGEND = {
    id: 'oa-trips-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Старты (сумма за период)' }),
    items: OA_TRIPS_GRADIENT,
};

/**
 * DA-12016: Легенда метрики "Флот" для операционных зон
 * Тип: GRADIENT
 */
export const OA_FLEET_GRADIENT_LEGEND = {
    id: 'oa-fleet-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Средний флот за период' }),
    items: OA_FLEET_GRADIENT,
};

/**
 * Зоны ограничений V2 легенда
 * Тип: COLORED_ITEMS
 * Иконка: LINE (для зон с обводкой)
 * Items подтягиваются из стиля слоя через styleKey
 */
export const RESTRICTION_ZONES_V2_LEGEND = {
    id: 'restriction-zones-v2',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Зонирование' }),
    icon: LEGEND_ICON_TYPES.LINE,
    style: RESTRICTION_ZONES_STYLE,
    styleKey: 'zones',
    renderConfig: {
        containerClass: 'innerLegendGrid',
        iconSize: '24px',
        iconOpacity: 0.8,
    },
};

// ============================================================
// ВСПОМОГАТЕЛЬНЫЕ ОБЪЕКТЫ ДЛЯ СОВМЕСТИМОСТИ
// ============================================================

// DA-12966: потерянные самокаты (LOST / DELETED)
export const LOST_DEVICES_LEGEND = {
    id: 'lost-devices',
    type: LEGEND_TYPES.COLORED_ITEMS,
    title: defineMessage({ message: 'Потерянные самокаты:' }),
    icon: LEGEND_ICON_TYPES.CIRCLE,
    style: LOST_DEVICES_STYLE,
    styleKey: 'categories',
    renderConfig: {
        iconSize: '14px',
        iconOpacity: 0.85,
    },
};

// DA-12966: Лосты на гексагонах — viridis градиент по cnt_total
// DA-12966: Агрегированные потери — статическая градиентная легенда
export const LOST_DEVICES_H3_LEGEND = {
    id: 'lost-devices-h3',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Кол-во событий потери в ячейке (ед.):' }),
    style: LOST_DEVICES_H3_STYLE,
    styleKey: 'total',
    renderConfig: {
        description: defineMessage({ message: 'По всем доступным данным (с 2023 г.)' }),
    },
};

/**
 * DA-16000: «Wifi точки», точечное представление — цвет по числу наблюдений.
 * Шкала статическая: число наблюдений у точки от зума не зависит.
 */
export const WIFI_AP_OBS_LEGEND = {
    id: 'wifi-ap-obs',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Сколько раз видели точку:' }),
    style: WIFI_AP_OBS_STYLE,
    styleKey: 'obs',
};

/**
 * DA-16000: «Плотность покрытия» — точек доступа в H3-ячейке.
 * Динамическая шкала: пороги пересчитываются множителем 4^(12−zoom), а не остаются
 * фиксированными. База — zoom 12, самый мелкий уровень: на нём легенда показывает
 * сырые значения, а на уровнях крупнее — умноженные.
 *
 * Множитель 4 подобран по данным, а не по геометрии H3 (где у ячейки 7 потомков):
 * фактический рост p90 по уровням 12→7 составляет 4, 15, 62, 302, 1654, 8612 — это
 * примерно вчетверо на уровень, потому что часть дочерних ячеек пуста.
 *
 * noAutoScale обязателен: без него рендерер считает baseValues тысячами и, увидев
 * масштабированное значение меньше единицы, молча домножает подписи на 1000 —
 * в ячейке 4 точки, а легенда рисует шкалу до 2K.
 */
export const WIFI_AP_H3_LEGEND = {
    id: 'wifi-ap-h3-gradient',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Точек доступа в ячейке' }),
    style: WIFI_AP_H3_STYLE,
    styleKey: 'density',
    isDynamic: true,
    dynamicConfig: {
        h3ZoomReduxKey: 'mapFilters.wifiApH3Zoom',
        baseZoom: 12,
        scalingFactor: 4,
        minZoom: 7,
        maxZoom: 12,
        baseValues: [ 1, 2, 4, 8, 16 ],
    },
    renderConfig: {
        noAutoScale: true,
    },
};

/**
 * Выбросы GPS-треков по H3-гексагонам — динамическая легенда (DA-14308)
 * Метрика: медиана дистанции выброса (м) или количество событий.
 * Масштабирование по зуму, пивот по src (ALL / GPS / LBS / PHONE_GPS / BLE_S).
 */
export const ROUTE_ANOMALIES_H3_LEGEND = {
    id: 'route-anomalies-h3',
    type: LEGEND_TYPES.GRADIENT,
    title: defineMessage({ message: 'Медиана дистанции выброса (м) в ячейке:' }),
    style: ROUTE_ANOMALIES_H3_STYLE,
    styleKey: 'medianDist',
    isDynamic: true,
    dynamicConfig: {
        h3ZoomReduxKey: 'mapFilters.routeAnomaliesH3Zoom',
        baseZoom: 12,
        scalingFactor: 1,
        minZoom: 6,
        maxZoom: 12,
        // 5 значений для 5 items: совпадают с paint стопами (0, 300, 420, 560, 700)
        // scalingFactor=1 — значения постоянны по зумам, только отображается H3 zoom из Redux
        baseValues: [ 0, 300, 420, 560, 700 ],
    },
    renderConfig: {},
};

export const ROUTE_ANOMALIES_H3_CNT_LEGEND = {
    id: 'route-anomalies-h3-cnt',
    type: LEGEND_TYPES.GRADIENT,
    // Paint: cnt * 7^(zoom-10), baseValues — стопы при zoom=10 (= raw cnt значения на zoom=10)
    // zoom=12: /49 → 0.2, 1, 3, 10, 41 (p50=1, p75=2, p90=3, p99=18)
    // zoom=10: 10, 50, 150, 500, 2000 (p50=4, p90=29, p99=167)
    // zoom=6: *2401 → 24K, 120K, 360K (p99=49K, max=193K)
    title: defineMessage({ message: 'Количество выбросов в ячейке:' }),
    style: ROUTE_ANOMALIES_H3_STYLE,
    styleKey: 'cnt',
    isDynamic: true,
    dynamicConfig: {
        h3ZoomReduxKey: 'mapFilters.routeAnomaliesH3Zoom',
        baseZoom: 10,
        scalingFactor: 7,
        minZoom: 6,
        maxZoom: 12,
        baseValues: [ 10, 50, 150, 500, 2000, 6000, 20000 ],
    },
    // noAutoScale: baseValues в сырых числах (не тысячах) — не умножать на 1000
    renderConfig: { noAutoScale: true },
};

/**
 * Карта легенд для быстрого доступа по ID
 */
export const LEGENDS_MAP = {
    [POI_LEGEND.id]: POI_LEGEND,
    [NEW_BUILDINGS_LEGEND.id]: NEW_BUILDINGS_LEGEND,
    [LANDUSE_LEGEND.id]: LANDUSE_LEGEND,
    [LANDUSE_NEW_LEGEND.id]: LANDUSE_NEW_LEGEND,
    [POPULATION_LEGEND.id]: POPULATION_LEGEND,
    [PARKING_RADIUS_LEGEND.id]: PARKING_RADIUS_LEGEND,
    [CLASSIFIED_PARKING_LEGEND.id]: CLASSIFIED_PARKING_LEGEND,
    [CLASS_PARKING_TYPES_LEGEND.id]: CLASS_PARKING_TYPES_LEGEND,
    [BIKE_PATH_LEGEND.id]: BIKE_PATH_LEGEND,
    [SCR_TRACKS_LEGEND.id]: SCR_TRACKS_LEGEND,
    [SUPPOSED_BIKE_PATH_LEGEND.id]: SUPPOSED_BIKE_PATH_LEGEND,
    [CLUSTERED_TAPS_LEGEND.id]: CLUSTERED_TAPS_LEGEND,
    [PARKING_PROPOSALS_LEGEND.id]: PARKING_PROPOSALS_LEGEND,
    [CLUSTERED_OPENS_LEGEND.id]: CLUSTERED_OPENS_LEGEND,
    [AGG_USER_OPENS_LEGEND_7DAYS.id]: AGG_USER_OPENS_LEGEND_7DAYS,
    [AGG_USER_OPENS_LEGEND_30DAYS.id]: AGG_USER_OPENS_LEGEND_30DAYS,
    [AGG_USER_TAPS_ZERO_STARTS_LEGEND_7DAYS.id]: AGG_USER_TAPS_ZERO_STARTS_LEGEND_7DAYS,
    [AGG_USER_TAPS_ZERO_STARTS_LEGEND_30DAYS.id]: AGG_USER_TAPS_ZERO_STARTS_LEGEND_30DAYS,
    [REG_TO_TRIP_CONVERSION_LEGEND.id]: REG_TO_TRIP_CONVERSION_LEGEND,
    [AGG_USER_OPENS_TO_STARTS_LEGEND.id]: AGG_USER_OPENS_TO_STARTS_LEGEND,
    [AGGREGATED_DIFF_START_FINISH_LEGEND.id]: AGGREGATED_DIFF_START_FINISH_LEGEND,
    [AGGREGATED_DIFF_START_FINISH_LEGEND_AUGUST.id]: AGGREGATED_DIFF_START_FINISH_LEGEND_AUGUST,
    [AGG_FAILED_TRIPS_LEGEND_7DAYS.id]: AGG_FAILED_TRIPS_LEGEND_7DAYS,
    [AGG_HEX_SPEED_LEGEND.id]: AGG_HEX_SPEED_LEGEND,
    [WALKSCORE_LEGEND.id]: WALKSCORE_LEGEND,
    [PARKING_DENSITY_LEGEND.id]: PARKING_DENSITY_LEGEND,
    [PARKING_DENSITY_GRADIENT_LEGEND.id]: PARKING_DENSITY_GRADIENT_LEGEND,
    [TRANSPORT_ISOCHRONES_LEGEND.id]: TRANSPORT_ISOCHRONES_LEGEND,
    [CUR_DEFICIT_SCOOTERS_LEGEND.id]: CUR_DEFICIT_SCOOTERS_LEGEND,
    [CUR_PROFICIT_SCOOTERS_LEGEND.id]: CUR_PROFICIT_SCOOTERS_LEGEND,
    [REBALANCE_PARKING_LEGEND.id]: REBALANCE_PARKING_LEGEND,
    [SLOPE_LEGEND.id]: SLOPE_LEGEND,
    [ASPECT_LEGEND.id]: ASPECT_LEGEND,
    [COMPOSITE_LEGEND.id]: COMPOSITE_LEGEND,
    [SMOOTHED_RENT_GRADIENT_LEGEND.id]: SMOOTHED_RENT_GRADIENT_LEGEND,
    [ROAD_ROUGHNESS_GRADIENT_LEGEND.id]: ROAD_ROUGHNESS_GRADIENT_LEGEND,
    [BOOKING_PARKING_GRADIENT_LEGEND.id]: BOOKING_PARKING_GRADIENT_LEGEND,
    [AGGREGATED_TRACKS_AUGUST_GRADIENT_LEGEND.id]: AGGREGATED_TRACKS_AUGUST_GRADIENT_LEGEND,
    [AGGREGATED_TRIPS_AUGUST_H3_GRADIENT_LEGEND.id]: AGGREGATED_TRIPS_AUGUST_H3_GRADIENT_LEGEND,
    [RECHARGING_STATS_H3_DYNAMIC_LEGEND.id]: RECHARGING_STATS_H3_DYNAMIC_LEGEND,
    [RECHARGING_DAILY_H3_DYNAMIC_LEGEND.id]: RECHARGING_DAILY_H3_DYNAMIC_LEGEND,
    [ADMIN_AGG_TRACKS_V2_LEGEND.id]: ADMIN_AGG_TRACKS_V2_LEGEND,
    [ADMIN_AGG_TRACKS_MONTHLY_LEGEND.id]: ADMIN_AGG_TRACKS_MONTHLY_LEGEND,
    [AGGREGATED_STARTS_AUGUST_GRADIENT_LEGEND.id]: AGGREGATED_STARTS_AUGUST_GRADIENT_LEGEND,
    [AGGREGATED_FINISHES_AUGUST_GRADIENT_LEGEND.id]: AGGREGATED_FINISHES_AUGUST_GRADIENT_LEGEND,
    [MEDIAN_SPEED_GRADIENT_LEGEND.id]: MEDIAN_SPEED_GRADIENT_LEGEND,
    [DYNAMIC_TARIFF_GRADIENT_LEGEND.id]: DYNAMIC_TARIFF_GRADIENT_LEGEND,
    [DYNAMIC_TARIFF_ZONES_LEGEND.id]: DYNAMIC_TARIFF_ZONES_LEGEND,
    [HOUSES_POPULATION_LEGEND.id]: HOUSES_POPULATION_LEGEND,
    [HOUSES_YEAR_LEGEND.id]: HOUSES_YEAR_LEGEND,
    [WHOOSH_PARKING_CAPACITY_LEGEND.id]: WHOOSH_PARKING_CAPACITY_LEGEND,
    [AGG_SCOOTERS_QUANTITY_LEGEND.id]: AGG_SCOOTERS_QUANTITY_LEGEND,
    [STARTS_BY_HOUR_COLORS_LEGEND.id]: STARTS_BY_HOUR_COLORS_LEGEND,
    [STARTS_BY_HOUR_SIZE_LEGEND.id]: STARTS_BY_HOUR_SIZE_LEGEND,
    [WHOOSH_HOTSPOT_LEGEND.id]: WHOOSH_HOTSPOT_LEGEND,
    [PARKING_DOWNTIME_48H_GRADIENT_LEGEND.id]: PARKING_DOWNTIME_48H_GRADIENT_LEGEND,
    [HEX_PARKINGS_GRADIENT_LEGEND.id]: HEX_PARKINGS_GRADIENT_LEGEND,
    [OPERATION_AREAS_ROTATION_GRADIENT_LEGEND.id]: OPERATION_AREAS_ROTATION_GRADIENT_LEGEND,
    [OPERATION_AREAS_ROTATION_2_GRADIENT_LEGEND.id]: OPERATION_AREAS_ROTATION_2_GRADIENT_LEGEND,
    [RESTRICTION_ZONES_V2_LEGEND.id]: RESTRICTION_ZONES_V2_LEGEND,
    [REBALANCE_FORECAST_LEGEND.id]: REBALANCE_FORECAST_LEGEND,
    [REBALANCE_FORECAST_SIZE_LEGEND.id]: REBALANCE_FORECAST_SIZE_LEGEND,
    [REBALANCE_DELTA_LEGEND.id]: REBALANCE_DELTA_LEGEND,
    [REBALANCE_DELTA_SIZE_LEGEND.id]: REBALANCE_DELTA_SIZE_LEGEND,
    [FACT_REBALANCE_COLORS_LEGEND.id]: FACT_REBALANCE_COLORS_LEGEND,
    [FACT_REBALANCE_SIZE_LEGEND.id]: FACT_REBALANCE_SIZE_LEGEND,
    [LOST_DEVICES_LEGEND.id]: LOST_DEVICES_LEGEND,
    [LOST_DEVICES_H3_LEGEND.id]: LOST_DEVICES_H3_LEGEND,
    [ROUTE_ANOMALIES_H3_LEGEND.id]: ROUTE_ANOMALIES_H3_LEGEND,
    [WIFI_AP_OBS_LEGEND.id]: WIFI_AP_OBS_LEGEND,
    [WIFI_AP_H3_LEGEND.id]: WIFI_AP_H3_LEGEND,
};

/**
 * Получить конфигурацию легенды по ID
 */
export const getLegendConfig = legendId => LEGENDS_MAP[legendId];
